import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/data/blogPosts.ext";
import { Calendar, Clock, ArrowRight } from "lucide-react";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogIndexPage = () => {
  const [active, setActive] = useState("All");

  const posts = useMemo(() => {
    const list = active === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === active);
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [active]);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <SEO
        title="Wyoming Moving Tips & Guides | Crosstown Movers Blog"
        description="Pro moving tips, packing guides, and pricing breakdowns from Wyoming's most experienced moving company. Helping Casper, Cheyenne, Laramie & beyond move smarter."
        path="/blog"
      />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-[var(--ct-primary)] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[var(--ct-secondary)]/10 blur-3xl pointer-events-none" />
        <div className="section-container relative">
          <div className="label-xs text-white mb-3">The Crosstown Movers Blog</div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl"
            style={{ fontFamily: "Outfit" }}
          >
            Practical advice from Wyoming's most experienced movers.
          </h1>
          <p className="mt-5 text-white/80 max-w-2xl text-lg leading-relaxed">
            Pricing breakdowns, packing tips, winter-moving guides, and honest insider advice
            from Casper movers with 46+ years of experience.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-10 bg-white sticky top-20 z-30 border-b border-[var(--ct-border)]" data-testid="blog-categories">
        <div className="section-container flex flex-wrap gap-2">
          {BLOG_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`blog-cat-${c}`}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                active === c
                  ? "bg-[var(--ct-primary)] text-[var(--ct-secondary)]"
                  : "bg-[var(--ct-bg)] text-[var(--ct-text-muted)] hover:bg-[var(--ct-accent)] hover:text-[var(--ct-primary)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="py-12 md:py-16 bg-[var(--ct-bg)]">
          <div className="section-container">
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid lg:grid-cols-12 gap-8 items-center bg-white rounded-3xl overflow-hidden border border-[var(--ct-border)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.1)] transition-all duration-300"
              data-testid="blog-featured"
            >
              <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto lg:h-[400px] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="lg:col-span-5 p-8 md:p-10">
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-3 py-1 rounded-full bg-[var(--ct-accent)] text-[var(--ct-primary)] font-semibold uppercase tracking-widest">
                    {featured.category}
                  </span>
                  <span className="flex items-center gap-1 text-[var(--ct-text-muted)]">
                    <Calendar className="w-3 h-3" /> {formatDate(featured.date)}
                  </span>
                  <span className="flex items-center gap-1 text-[var(--ct-text-muted)]">
                    <Clock className="w-3 h-3" /> {featured.readMin} min read
                  </span>
                </div>
                <h2
                  className="mt-5 text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight leading-[1.15] group-hover:text-[var(--ct-secondary)] transition"
                  style={{ fontFamily: "Outfit" }}
                >
                  {featured.title}
                </h2>
                <p className="mt-4 text-[var(--ct-text-muted)] leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-2 font-semibold text-[var(--ct-secondary)] group-hover:gap-3 transition-all">
                  Read article <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-12 md:py-16 bg-white">
        <div className="section-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex flex-col rounded-2xl overflow-hidden bg-[var(--ct-bg)] border border-[var(--ct-border)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-300"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] text-[var(--ct-text-muted)] uppercase tracking-widest font-semibold">
                  <span className="text-[var(--ct-secondary)]">{p.category}</span>
                  <span>·</span>
                  <span>{p.readMin} min</span>
                </div>
                <h3
                  className="mt-3 text-xl font-semibold text-[var(--ct-primary)] tracking-tight leading-snug group-hover:text-[var(--ct-secondary)] transition"
                  style={{ fontFamily: "Outfit" }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--ct-text-muted)] leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--ct-secondary)] group-hover:gap-2 transition-all">
                  Read more <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
};

export default BlogIndexPage;
